import React, { useState, useRef, useEffect } from "react";
import { PageWrapper } from "../material-ledger-summary";
import { LoaderComponent } from "../../../../shared/components/loaders/page-loader";
import { Box, Typography } from "@mui/material";
import FormBuilder from "../../../../shared/components/form-builder/form-builder";
import { StyledButton } from "../../../../shared/components/buttons/custom-button";
import { NavLink } from "react-router-dom";
import * as Yup from "yup";
import RawMaterialDetails from "./raw-material-details";
import { CustomButton } from "../../../../shared/components/buttons/buttons";
import ModalPopup from "../../../../shared/components/modals/custom-popup";
import { dtrackItService } from "../../../transaction-certificates/tc-service/dtrackit.service";
import {
  buyerSellerDetailsService,
  mlbuyerSellerDetailsService,
} from "../../../transaction-certificates/tc-service/buyer-seller-details.service";
import useNotification from "../../../../shared/hooks/useNotification";
import { mapFormToPayload } from "./rmdf-apis";
import { useApi } from "../../../../shared/hooks/useApi";
import { MLAppConfig } from "../../ml-models/MLAppConfig";
import { useTransactionCertificateStore } from "../../../../store/transaction-certificates.store";

export interface IRawMaterialDetail {
  rawMaterialCode: string;
  rawMaterialPercentage: string;
}
/** Interfaces for Form Values */
export interface SourceRawMaterialSupplier {
  organizationName: string;
  teId: string;
  address: string;
  state: string;
  country: string;
}

export interface RMDFMDFFormValues {
  mdfId: string;
  rmdfMdfNumber: string;
  coTeID: string;
  coName: string;
  shipmentNumber: string;
  shipmentDate: string;
  invoiceDate: string;
  orderNumber: string;
  productCategory: string;
  productDetail: string;
  weightInKgs: string;
  organizationName: string;
  teId: string;
  address: string;
  state: string;
  country: string;
  rawMaterialDetails: IRawMaterialDetail[];
}

/** Validation Schema */
const rmdfMdfValidationSchema = Yup.object().shape({
  mdfId: Yup.string(),
  rmdfMdfNumber: Yup.string().required("RMDF/MDF Number is required"),
  coTeID: Yup.string().required("CO TE-ID is required"),
  coName: Yup.string().required("Certified Organization Name is required"),
  shipmentNumber: Yup.string().required("Shipment Number is required"),
  shipmentDate: Yup.string().required("Shipment Date is required"),
  invoiceDate: Yup.string().required("Invoice Date is required"),
  orderNumber: Yup.string().optional(),
  productCategory: Yup.string().required("Product Category is required"),
  productDetail: Yup.string().required("Product Detail is required"),
  weightInKgs: Yup.string()
    .required("Weight (kg) is required")
    .matches(/^\d+(\.\d{1,2})?$/, "Weight must be a valid number"),
  organizationName: Yup.string().required("Organization Name is required"),
  teId: Yup.string().required("TE-ID is required"),
  address: Yup.string().required("Address is required"),
  state: Yup.string().required("State is required"),
  country: Yup.string().required("Country is required"),
  rawMaterialDetails: Yup.array().of(
    Yup.object().shape({
      rawMaterialCode: Yup.string().required("Raw Material Code is required"),
      rawMaterialPercentage: Yup.string()
        .required("Percentage is required")
        .matches(/^\d+(\.\d{1,2})?$/, "Percentage must be a valid number")
        .test("percentage", "Percentage must be between 0 and 100", (value) => {
          if (!value) return false;
          const num = parseFloat(value);
          return num >= 0 && num <= 100;
        }),
    })
  ),
});

/** Add these functions before the RmdfForm component */

const RmdfForm: React.FC = () => {
  const [stepData, setStepData] = useState({
    rawMaterialDetails: [] as IRawMaterialDetail[],
  });
  const [editingRowIndex, setEditingRowIndex] = useState<number>(-1);
  const { cbLicensingCode } = useTransactionCertificateStore();
  /** Initial Form Values */
  const [initialValues] = useState<RMDFMDFFormValues>({
    mdfId: "123",
    rmdfMdfNumber: "",
    coTeID: "",
    coName: "",
    shipmentNumber: "",
    shipmentDate: "",
    invoiceDate: "",
    orderNumber: "",
    productCategory: "",
    productDetail: "",
    weightInKgs: "",
    organizationName: "",
    teId: "",
    address: "",
    state: "",
    country: "",
    rawMaterialDetails: [],
  });

  /** Form Reference */
  const formRef = useRef<any>(null);
  /** Add dialog state */
  const [rawMaterialDialogOpen, setRawMaterialDialogOpen] = useState(false);
  const { notify } = useNotification();
  /** Add these state variables at the top of the component */
  const [productCategories, setProductCategories] = useState([]);
  const [productDetails, setProductDetails] = useState([]);
  const fetchTENameDetails = async (
    query: string,
    offset: number,
    type: string
  ) => {
    try {
      const queryParams: Record<string, any> = {
        query_param: "",
        offset: offset,
        name: query,
        tcCbLicensingCode: cbLicensingCode,
      };
      const response = await mlbuyerSellerDetailsService.getMLCODetails(
        queryParams
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching TE details by name:", error);
      return [];
    }
  };

  const fetchTEIdDetails = async (
    query: string,
    offset: number,
    type: string
  ) => {
    try {
      const queryParams: Record<string, any> = {
        query_param: query,
        offset: offset,
        name: "",
        tcCbLicensingCode: cbLicensingCode,
      };
      const response = await mlbuyerSellerDetailsService.getMLCODetails(
        queryParams
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching TE details by ID:", error);
      return [];
    }
  };
  // Add new state variables for visibility and disable control
  const [visible, setVisible] = useState({
    coName: true,
    coTeId: true,
    coNameLabel: true,
    coTeIdLabel: true,
  });

  const [disable, setDisable] = useState({
    coName: false,
    coTeId: false,
  });

  // Add new state for country and state options
  const [countryOptions, setCountryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const { executePost, loading } = useApi();
  /** Add this useEffect to fetch initial data */
  useEffect(() => {
    const getProductCategory = async () => {
      try {
        const result = await dtrackItService.getProductCategory();
        const prdtCategory = result.data.map((t: any) => ({
          value: t.code_1,
          label: `${t.description_1} (${t.code_1})`,
        }));
        setProductCategories(prdtCategory);
      } catch (err) {
        console.error("Error fetching Product Categories:", err);
      }
    };

    const getProductDetails = async () => {
      try {
        const result = await dtrackItService.getProductDetailsCode();
        const prdtDetails = result.data.map((t: any) => ({
          value: t.code_1,
          label: `${t.description_1} (${t.code_1})`,
        }));
        setProductDetails(prdtDetails);
      } catch (err) {
        console.error("Error fetching Product Details:", err);
      }
    };

    getProductCategory();
    getProductDetails();
  }, []);

  // Add functions to fetch country and state data
  const getCountry = async () => {
    try {
      const result = await dtrackItService.getCountry();
      const countries = result.data.map((t: any) => ({
        value: t.code_1,
        label: `${t.description_1} (${t.code_1})`,
      }));
      setCountryOptions(countries);
    } catch (err) {
      console.error("Failed to fetch countries:", err);
    }
  };

  const getState = async (countryCode: string) => {
    try {
      setIsLoadingStates(true);
      const result = await dtrackItService.getStateBasedonCountry(countryCode);
      const states = result.data.map((t: any) => ({
        value: t.code_1,
        label: `${t.description_1} (${t.code_1})`,
      }));
      setStateOptions(states);
    } catch (err) {
      setStateOptions([]);
    } finally {
      setIsLoadingStates(false);
    }
  };

  // Add useEffect to fetch countries on component mount
  useEffect(() => {
    getCountry();
  }, []);

  /** Add handler for raw material dialog */
  const handleRawMaterialDialogOpen = () => {
    setRawMaterialDialogOpen(true);
    setStepData((prev) => ({ ...prev }));
  };

  /** Update the handleCoNameValueChange and handleCoTeIdValueChange functions */
  const handleCoNameValueChange = (value: any) => {
    if (value) {
      formRef.current.setFieldValue("coName", value.account_name);
      formRef.current.setFieldValue("coTeID", value.TE_ID);
      setVisible((prev) => ({
        ...prev,
        coTeId: false,
        coNameLabel: false,
      }));
      setDisable((prev) => ({
        ...prev,
        coTeId: true,
      }));
    } else {
      formRef.current.setFieldValue("coName", "");
      formRef.current.setFieldValue("coTeID", "");
      setVisible((prev) => ({
        ...prev,
        coTeId: true,
        coNameLabel: true,
      }));
      setDisable((prev) => ({
        ...prev,
        coTeId: false,
      }));
    }
  };

  const handleCoTeIdValueChange = (value: any) => {
    if (value) {
      formRef.current.setFieldValue("coTeID", value.TE_ID);
      formRef.current.setFieldValue("coName", value.account_name);
      setVisible((prev) => ({
        ...prev,
        coName: false,
        coTeIdLabel: false,
      }));
      setDisable((prev) => ({
        ...prev,
        coName: true,
      }));
    } else {
      formRef.current.setFieldValue("coTeID", "");
      formRef.current.setFieldValue("coName", "");
      setVisible((prev) => ({
        ...prev,
        coName: true,
        coTeIdLabel: true,
      }));
      setDisable((prev) => ({
        ...prev,
        coName: false,
      }));
    }
  };

  /** Form Configuration */
  const formConfig = {
    pageName: "RMDF-MDF-Submission",
    initialValues,
    validationSchema: rmdfMdfValidationSchema,
    sections: [
      {
        title: "Raw Material Declaration Form",
        fields: [
          {
            label: "Certified Organization Name",
            name: "coName",
            type: "search",
            gridSize: { xs: 12, md: 4 },
            required: true,
            visible: (value: any) => visible.coName,
            props: {
              placeholder: "Search Organization Name",
              variant: "outlined",
              fullWidth: true,
              displayKey: "account_name",
              onChange: handleCoNameValueChange,
              onSearch: (query: string, offset: number) => {
                return fetchTENameDetails(query, offset, "name");
              },
            },
          },
          {
            label: "Certified Organization Name",
            name: "coName",
            type: "text",
            gridSize: { xs: 12, md: 4 },
            required: true,
            visible: (value: any) => !visible.coName,
            disabled: disable.coName,
            props: {
              placeholder: "Enter Organization Name",
              variant: "outlined",
              fullWidth: true,
            },
          },
          {
            label: "CO TE-ID",
            name: "coTeID",
            type: "search",
            gridSize: { xs: 12, md: 4 },
            required: true,
            visible: (value: any) => visible.coTeId,
            props: {
              placeholder: "Search by TE-ID",
              variant: "outlined",
              fullWidth: true,
              displayKey: "TE_ID",
              restrictInputFormat: visible.coTeIdLabel,
              onChange: handleCoTeIdValueChange,
              onSearch: (query: string, offset: number) => {
                return fetchTEIdDetails(query, offset, "query");
              },
            },
          },
          {
            label: "CO TE-ID",
            name: "coTeID",
            type: "text",
            gridSize: { xs: 12, md: 4 },
            required: true,
            visible: (value: any) => !visible.coTeId,
            disabled: disable.coTeId,
            props: {
              placeholder: "Enter TE-ID",
              variant: "outlined",
              fullWidth: true,
            },
          },
          {
            label: "RMDF/MDF Number",
            name: "rmdfMdfNumber",
            type: "text",
            gridSize: { xs: 12, md: 4 },
            required: true,
            props: {
              placeholder: "Enter RMDF/MDF Number",
              variant: "outlined",
              fullWidth: true,
            },
          },
          {
            label: "Shipment Number",
            name: "shipmentNumber",
            type: "text",
            gridSize: { xs: 12, md: 4 },
            required: true,
            props: {
              placeholder: "Enter Shipment Number",
              variant: "outlined",
              fullWidth: true,
            },
          },
          {
            label: "Shipment Date",
            name: "shipmentDate",
            type: "date",
            gridSize: { xs: 12, md: 4 },
            required: true,
            props: {
              placeholder: "Enter Shipment Date",
              variant: "outlined",
              fullWidth: true,
            },
          },
          {
            label: "Invoice Date",
            name: "invoiceDate",
            type: "date",
            gridSize: { xs: 12, md: 4 },
            required: true,
            props: {
              placeholder: "YYYY/MM/DD",
              variant: "outlined",
              fullWidth: true,
            },
          },
          {
            label: "Order Number",
            name: "orderNumber",
            type: "text",
            gridSize: { xs: 12, md: 4 },
            required: false,
            props: {
              placeholder: "Enter Order Number",
              variant: "outlined",
              fullWidth: true,
            },
          },
          {
            label: "Product Category",
            name: "productCategory",
            type: "select",
            gridSize: { xs: 12, md: 4 },
            required: true,
            props: {
              placeholder: "Select Product Category",
              variant: "outlined",
              fullWidth: true,
              enableSearch: true,
              options: productCategories,
            },
          },
          {
            label: "Product Detail",
            name: "productDetail",
            type: "select",
            gridSize: { xs: 12, md: 4 },
            required: true,
            props: {
              placeholder: "Select Product Detail",
              variant: "outlined",
              fullWidth: true,
              enableSearch: true,
              options: productDetails,
            },
          },
          {
            label: "Weight (kg)",
            name: "weightInKgs",
            type: "text",
            gridSize: { xs: 12, md: 10 },
            required: true,
            props: {
              placeholder: "Enter Weight in kg",
              variant: "outlined",
              fullWidth: true,
            },
          },
          {
            label: "",
            name: "rawMaterialDetails",
            type: "component",
            gridSize: { xs: 2, md: 2, lg: 2 },
            required: true,
            props: {},
            children: (
              <>
                <CustomButton
                  sx={{
                    marginTop: "22px",
                    borderRadius: "30px",
                    padding: "1 3",
                    marginRight: 2,
                    textTransform: "none",
                    backgroundColor: "#F0CD7F",
                    color: "#1C1C1C",
                    fontWeight: "400",
                    "&:hover": {
                      backgroundColor: "#E0B86F",
                      color: "#000000",
                      boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.15)",
                    },
                  }}
                  onClick={handleRawMaterialDialogOpen}
                  variant="contained"
                  placement="center"
                  size="small"
                  errorMessage=""
                  label={
                    stepData?.rawMaterialDetails.length === 0 ? (
                      <>
                        Add Raw Material Details
                        <span style={{ color: "#9E362C" }}></span> <br />
                      </>
                    ) : (
                      <>
                        Edit Raw Material Details
                        <span style={{ color: "#9E362C" }}></span>
                      </>
                    )
                  }
                />
                <span
                  style={{
                    fontSize: "0.8em",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  {stepData?.rawMaterialDetails.length} Records Added
                </span>
              </>
            ),
          },
        ],
      },
      {
        title: "Source (For Raw Material Supplier)",
        fields: [
          {
            label: "Organization Name",
            name: "organizationName",
            type: "text",
            gridSize: { xs: 12, md: 4 },
            required: true,
            props: {
              placeholder: "Enter Organization Name",
              variant: "outlined",
              fullWidth: true,
            },
          },
          {
            label: "TE-ID",
            name: "teId",
            type: "text",
            gridSize: { xs: 12, md: 4 },
            required: true,
            props: {
              placeholder: "Enter TE-ID",
              variant: "outlined",
              fullWidth: true,
            },
          },
          {
            label: "Address",
            name: "address",
            type: "text",
            gridSize: { xs: 12, md: 4 },
            required: true,
            props: {
              placeholder: "Enter Address",
              variant: "outlined",
              fullWidth: true,
            },
          },
          {
            label: "Country",
            name: "country",
            type: "select",
            gridSize: { xs: 12, md: 4 },
            required: true,
            events: {
              onChange: (newValue: any) => {
                let countryCode = JSON.stringify(
                  `[${[newValue].map((item) => `'${item}'`)}]`
                ).replace(/^"|"$/g, "");
                getState(countryCode);
                setDisable((prev) => ({
                  ...prev,
                  state: false,
                }));
              },
            },
            props: {
              options: countryOptions,
              enableSearch: true,
              placeholder: "Select Country",
              variant: "outlined",
              fullWidth: true,
            },
          },
          {
            label: "State",
            name: "state",
            type: "select",
            gridSize: { xs: 12, md: 4 },
            required: true,
            disabled: (values: any) => !values.country,
            props: {
              options: stateOptions,
              enableSearch: true,
              placeholder: "Select State",
              variant: "outlined",
              fullWidth: true,
              loading: isLoadingStates,
            },
          },
        ],
      },
    ],
    handleSubmit: async (values: RMDFMDFFormValues) => {
      try {
        const payload = {
          ...values,
          rawMaterialDetails: stepData.rawMaterialDetails,
        };
        const mappedPayload = mapFormToPayload(payload);
        const result = await executePost(
          `${MLAppConfig.RMDF_MDF_DETAILS}?teId=${values.coTeID}&tcCbLicensingCode=${cbLicensingCode}&mdfId=${values.mdfId}`,
          mappedPayload
        );
        if (result?.status === 200) {
          notify("Saved Successfully", "success");
          formRef.current.resetForm();
        } else {
          notify("Unable to Save", "error");
        }
      } catch (error) {
        console.error("Error saving RMDF/MDF Details:", error);
        notify("Error saving details", "error");
      }
    },
  };

  /** Add state for raw material details */

  /** Add modal for raw material details */

  const updateField = (fieldName: string, value: any) => {
    setStepData((prev) => ({ ...prev, [fieldName]: value }));
  };

  const handleDataUpdate = (field: string, data: any) => {
    setStepData((prev) => ({ ...prev, [field]: data }));
  };

  const handleSubmit = async () => {
    try {
      if (formRef.current) {
        console.log("Form Submitted:", formRef.current);
        formRef.current.submitForm(); // Programmatically submit the form
      }
      // Validate form
      await formRef.current.submitForm();
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  return (
    <PageWrapper>
      {loading ? (
        <LoaderComponent />
      ) : (
        <>
          <Typography
            mt={4}
            variant="h6"
            gutterBottom
            align="left"
            fontWeight="600"
          >
            RMDF Submission - Reclaimed Material Declaration Form
          </Typography>
          <FormBuilder formConfig={formConfig} formRef={formRef} />
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <NavLink to="/">
              <StyledButton variant="contained">Cancel</StyledButton>
            </NavLink>
            <StyledButton variant="contained" onClick={handleSubmit}>
              Save
            </StyledButton>
          </Box>
          <ModalPopup
            open={rawMaterialDialogOpen}
            onSave={() => {
              formRef.current.setFieldValue(
                "rawMaterialDetails",
                stepData.rawMaterialDetails
              );
              updateField("rawMaterialDetails", stepData.rawMaterialDetails);
              setRawMaterialDialogOpen(false);
            }}
            onClose={() => setRawMaterialDialogOpen(false)}
          >
            <RawMaterialDetails
              rawMaterialData={stepData.rawMaterialDetails}
              setRawMaterialData={(data) =>
                handleDataUpdate("rawMaterialDetails", data)
              }
              editingRowIndex={editingRowIndex}
              setEditingRowIndex={setEditingRowIndex}
            />
          </ModalPopup>
        </>
      )}
    </PageWrapper>
  );
};

export default RmdfForm;
